import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../config/api';
import { RootState } from '../config/store';

// --- TYPE DEFINITIONS ---

interface ErrorType {
    message: string;
}

interface WorkOrder {
    id: string;
    description: string;
    done: boolean;
    // Add any other properties of a work order here
    [key: string]: any;
}

// --- ASYNC THUNKS (SIMPLIFIED) ---

export const fetchWorkOrders = createAsyncThunk<WorkOrder[]>(
  'workOrders/fetchWorkOrders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/WorkOrders`);
      return response.data.items || response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const fetchWorkOrderById = createAsyncThunk<WorkOrder, string>(
  'workOrders/fetchWorkOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/WorkOrders/${id}`); // Corrected endpoint casing from workOrders to WorkOrders
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }
);

// Assuming the payload for creation doesn't include an ID
export const createWorkOrder = createAsyncThunk<WorkOrder, Omit<WorkOrder, 'id'>>(
  'workOrders/createWorkOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/WorkOrder', payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const updateWorkOrder = createAsyncThunk<WorkOrder, WorkOrder>(
  'workOrders/updateWorkOrder',
  async (workOrderPayload, { rejectWithValue }) => {
    try {
      const response = await api.put(`/WorkOrder/${workOrderPayload.id}`, workOrderPayload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// --- SLICE DEFINITION ---

interface WorkOrdersState {
  workOrders: WorkOrder[];
  workOrderById: WorkOrder | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed'; // Legacy
  listStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  detailsStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  saveStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: ErrorType | null;
}

const initialState: WorkOrdersState = {
  workOrders: [],
  workOrderById: null,
  status: 'idle',
  listStatus: 'idle',
  detailsStatus: 'idle',
  saveStatus: 'idle',
  error: null,
};

const workOrdersSlice = createSlice({
  // ✅ FIX: Corrected slice name to be unique
  name: 'workOrders',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkOrders.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchWorkOrders.fulfilled, (state, action: PayloadAction<WorkOrder[]>) => {
        state.listStatus = 'succeeded';
        state.workOrders = action.payload;
      })
      .addCase(fetchWorkOrders.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(fetchWorkOrderById.pending, (state) => {
        state.detailsStatus = 'loading';
      })
      .addCase(fetchWorkOrderById.fulfilled, (state, action: PayloadAction<WorkOrder>) => {
        state.detailsStatus = 'succeeded';
        state.workOrderById = action.payload;
      })
      .addCase(fetchWorkOrderById.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(createWorkOrder.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(createWorkOrder.fulfilled, (state, action: PayloadAction<WorkOrder>) => {
        state.saveStatus = 'succeeded';
        state.workOrders.push(action.payload);
      })
      .addCase(createWorkOrder.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.payload as ErrorType;
      })
      .addCase(updateWorkOrder.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(updateWorkOrder.fulfilled, (state, action: PayloadAction<WorkOrder>) => {
        state.saveStatus = 'succeeded';
        const index = state.workOrders.findIndex(wo => wo.id === action.payload.id);
        if (index !== -1) {
          state.workOrders[index] = action.payload;
        }
        if (state.workOrderById?.id === action.payload.id) {
          state.workOrderById = action.payload;
        }
      })
      .addCase(updateWorkOrder.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.payload as ErrorType;
      });
  },
});

export default workOrdersSlice.reducer;